import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { useFetchLandingPacks } from '../../../hooks/useFetchtLandingPacks';
import { InjectAppServices } from '../../../services/pure-di';
import { Loading } from '../../Loading/Loading';
import HeaderSection from '../../shared/HeaderSection/HeaderSection';
import { AddOnGoBackSection } from '../AddOnGoBackSection';
import * as S from './styles';
import { ShoppingCart } from '../ShoppingCart';
import { UnexpectedError } from '../UnexpectedError';
import { LandingPacks, filterPackagesEqualOrGreatherToZero } from './LandingPacks';
import { FormattedMessage, useIntl } from 'react-intl';
import { Navigate } from 'react-router-dom';
import {
  DELETE_LANDING_PAGES_ACTIONS,
  INITIAL_STATE_DELETE_LANDING_PAGES,
  deleteLandingPagesReducer,
} from './reducers/deleteLandingPagesReducer';
import { LandingPacksMessages } from './LandingPacksMessages';
import { ACCOUNT_TYPE, FREE_ACCOUNT } from '../../../utils';
import { AddOnType, BUY_LANDING_PACK, PaymentMethodType } from '../../../doppler-types';
import { getPromotionInformationMessage } from '../utils';

export const paymentFrequenciesListForLandingPacks = [
  {
    numberMonths: 12,
    subscriptionType: 'yearly',
    discountPercentage: 25,
  },
  {
    numberMonths: 6,
    subscriptionType: 'half_yearly',
    discountPercentage: 15,
  },
  {
    numberMonths: 3,
    subscriptionType: 'quarterly',
    discountPercentage: 5,
  },
  {
    numberMonths: 1,
    subscriptionType: 'monthly',
    discountPercentage: 0,
  },
];

const verifyIsTheSameLandingPacks = (contractedLandingPages, selectedLandingPacks) => {
  if (!contractedLandingPages && selectedLandingPacks?.length > 0) {
    return false;
  }

  if (contractedLandingPages?.length !== selectedLandingPacks?.length) {
    return false;
  }

  const finded = contractedLandingPages?.find(
    (clp) =>
      !selectedLandingPacks?.find(
        (slp) => slp.planId === clp.idPlan && slp.packagesQty === clp.packageQty,
      ),
  );
  return !finded;
};

export const LandingPacksSelection = InjectAppServices(
  ({
    dependencies: {
      appSessionRef,
      dopplerAccountPlansApiClient,
      dopplerBillingUserApiClient,
      dopplerLegacyClient,
    },
  }) => {
    const intl = useIntl();
    const _ = (id, values) => intl.formatMessage({ id: id }, values);
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
    const [selectedLandingPacks, setSelectedLandingPacks] = useState(null);
    const [landingPacksFormValues, setLandingPacksFormValues] = useState([]);
    const [showArchiveLandings, setShowArchiveLandings] = useState(false);
    const [isDowngrade, setIsDowngrade] = useState(false);
    const [cancelLandings, setCancelLandings] = useState(false);
    const [showPromotionInformation, setShowPromotionInformation] = useState(false);
    const formRef = useRef();
    const {
      error,
      loading,
      landingPacks: allLandingPacks,
    } = useFetchLandingPacks(dopplerAccountPlansApiClient);
    const [
      {
        loading: loadingRemoveLandingPages,
        error: errorRemoveLandingPages,
        removed: landingPagesRemoved,
      },
      dispatch,
    ] = useReducer(deleteLandingPagesReducer, INITIAL_STATE_DELETE_LANDING_PAGES);
    const selectedLandingPacksRef = useRef(null);
    selectedLandingPacksRef.current = selectedLandingPacks;
    const isDowngradeRef = useRef(null);
    isDowngradeRef.current = isDowngrade;
    const numberOfPublishedLandingsRef = useRef(null);

    const sessionPlan = appSessionRef.current.userData.user;
    const contractedLandingPagesRef = useRef(
      appSessionRef.current.userData.user.landings?.landingPacks,
    );
    const isMonthlySubscription = sessionPlan.plan.planSubscription === 1;
    const landingsEditorEnabled = appSessionRef?.current?.userData?.features?.landingsEditorEnabled;
    const { isFreeAccount } = sessionPlan.plan;
    const { billingCountry } = sessionPlan;

    const landingPagesPromotions = useMemo(
      () =>
        appSessionRef.current.userData.user.addOnPromotions !== undefined
          ? appSessionRef.current.userData.user.addOnPromotions.filter(
              (aop) => aop.idAddOnType === AddOnType.Landings,
            )
          : [],
      [appSessionRef],
    );

    const handleRemove = () => {
      const { resetForm } = formRef.current;
      setSelectedLandingPacks([]);
      resetForm && resetForm();
    };

    useEffect(() => {
      const fetchData = async () => {
        const paymentMethodData = await dopplerBillingUserApiClient.getPaymentMethodData();

        let paymentMethod = paymentMethodData.success
          ? paymentMethodData.value
          : { paymentMethodName: PaymentMethodType.creditCard };

        setSelectedPaymentMethod(paymentMethod.paymentMethodName);
      };
      fetchData();
    }, [dopplerBillingUserApiClient]);

    useEffect(() => {
      const fetchLandingPagesPublished = async () => {
        const numberOfPublishedLandings = await dopplerLegacyClient.getLandingPagesAmount(
          appSessionRef.current.userData.user.idUser,
        );
        numberOfPublishedLandingsRef.current = numberOfPublishedLandings;
      };

      fetchLandingPagesPublished();
    }, [dopplerLegacyClient, appSessionRef]);

    const handleSave = useCallback(
      (landingPacks) => {
        setSelectedLandingPacks(landingPacks);

        var hasLandingPageWithPromotion;
        landingPacks.forEach((element) => {
          if (!hasLandingPageWithPromotion) {
            hasLandingPageWithPromotion = landingPagesPromotions.filter(
              (lp) => lp?.idAddOnPlan === element.planId,
            )[0];
          }
        });

        const landingPackPromotion = landingPagesPromotions.filter(
          (pn) =>
            pn?.idAddOnPlan !== undefined &&
            pn?.idAddOnPlan !== hasLandingPageWithPromotion?.idAddOnPlan,
        )[0];

        setShowPromotionInformation(landingPackPromotion && landingPacks.length > 0);
      },
      [landingPagesPromotions],
    );

    const handleLandingPagesDowngrade = useCallback(
      async (isDowngradeWithSelectedLandings) => {
        const _isDowngrade =
          isDowngradeWithSelectedLandings ||
          (contractedLandingPagesRef?.current?.length > 0 &&
            selectedLandingPacksRef.current?.length === 0);
        if (_isDowngrade) {
          const numberOfSelectedLandings =
            selectedLandingPacksRef.current.reduce(
              (landingsAmount, lp) => landingsAmount + lp.landingsQty * lp.packagesQty,
              0,
            ) || 0;
          const numberOfPublishedLandings = await dopplerLegacyClient.getLandingPagesAmount(
            appSessionRef.current.userData.user.idUser,
          );
          setShowArchiveLandings(numberOfPublishedLandings > numberOfSelectedLandings);
        } else {
          setShowArchiveLandings(false);
        }
        setIsDowngrade(_isDowngrade);
      },
      [dopplerLegacyClient, appSessionRef],
    );

    useEffect(() => {
      if (allLandingPacks?.length > 0) {
        if (
          contractedLandingPagesRef?.current?.length > 0 &&
          !selectedLandingPacksRef?.current?.length
        ) {
          const landingPacks = allLandingPacks?.map((lp) => {
            const landingPackFinded = contractedLandingPagesRef?.current.find(
              (ilp) => ilp.idPlan === lp.planId,
            );
            return { ...lp, packagesQty: landingPackFinded ? landingPackFinded.packageQty : 0 };
          });
          handleSave(filterPackagesEqualOrGreatherToZero(landingPacks));
          setLandingPacksFormValues(landingPacks);
        } else {
          const landingPacks = allLandingPacks?.map((lp) => {
            return { ...lp, packagesQty: 0 };
          });
          setLandingPacksFormValues(landingPacks);
        }
      }
    }, [allLandingPacks, handleSave, isDowngradeRef]);

    const handleRemoveLandings = () => {
      const resetLandingForm = async () => {
        if (contractedLandingPagesRef.current?.length > 0) {
          setLandingPacksFormValues(allLandingPacks?.map((lp) => ({ ...lp, packagesQty: 0 })));
          dispatch({
            type: DELETE_LANDING_PAGES_ACTIONS.REMOVED,
          });
        }
        handleRemove();
      };

      if (numberOfPublishedLandingsRef.current > 0) {
        setShowArchiveLandings(true);
      } else {
        resetLandingForm();
        setCancelLandings(true);
      }
    };

    if (isFreeAccount) {
      return <Navigate to={`/plan-types?${ACCOUNT_TYPE}=${FREE_ACCOUNT}`} />;
    }

    if (!landingsEditorEnabled) {
      return <Navigate to="/dashboard" />;
    }

    if (loading) {
      return <Loading page />;
    }

    if (error) {
      return <UnexpectedError />;
    }

    return (
      <>
        <div className="dp-container dp-add-on-selection p-t-24">
          <AddOnGoBackSection />
        </div>
        <HeaderSection className="dp-add-on-selection">
          <div className="col-sm-12 col-md-12 col-lg-12">
            <h2 className="dp-first-order-title">{_('landing_selection.title')}</h2>
          </div>
        </HeaderSection>
        <div className="dp-container p-b-48 dp-add-on-selection">
          <div className="dp-rowflex">
            <div className="col-lg-8 col-md-12">
              <S.LandingInformation className="dp-container p-b-48">
                <div className="dp-rowflex">
                  <div className="col-sm-12">
                    <p>{_('landing_selection.description')}</p>
                  </div>
                  <div className="col-sm-6 m-t-36 dp-landing-information-column">
                    <h6>
                      <strong>{_('landing_selection.subtitle1')}</strong>
                    </h6>
                    <p>
                      <FormattedMessage
                        id={'landing_selection.description1'}
                        values={{
                          strong: (chunks) => <strong>{chunks}</strong>,
                        }}
                      />
                    </p>
                  </div>
                  <div className="col-sm-6 m-t-36 dp-landing-information-column">
                    <h6>
                      <strong>{_('landing_selection.subtitle2')}</strong>
                    </h6>
                    <p>
                      <FormattedMessage
                        id={'landing_selection.description2'}
                        values={{
                          strong: (chunks) => <strong>{chunks}</strong>,
                        }}
                      />
                    </p>
                  </div>
                </div>
              </S.LandingInformation>
              <LandingPacks
                landingPacks={landingPacksFormValues}
                handleSave={handleSave}
                formRef={formRef}
                isDowngrade={isDowngrade}
                handleRemoveLandings={handleRemoveLandings}
                showArchiveLandings={showArchiveLandings}
                loadingRemoveLandingPages={loadingRemoveLandingPages}
              />
              <LandingPacksMessages
                showArchiveLandings={showArchiveLandings}
                loadingRemoveLandingPages={loadingRemoveLandingPages}
                errorRemoveLandingPages={errorRemoveLandingPages}
              />
              {showPromotionInformation && (
                <section className="m-t-12">
                  <div className="dp-wrap-message dp-wrap-info">
                    <span className="dp-message-icon"></span>
                    <div className="dp-content-message dp-content-full">
                      <p>
                        {getPromotionInformationMessage(
                          'landing_selection',
                          appSessionRef.current.userData.user,
                          landingPagesPromotions,
                        )}
                      </p>
                    </div>
                  </div>
                </section>
              )}
            </div>
            <div className="col-lg-4 col-sm-12">
              <ShoppingCart
                discountConfig={{
                  paymentFrequenciesList: paymentFrequenciesListForLandingPacks,
                  selectedPaymentFrequency: paymentFrequenciesListForLandingPacks.find(
                    (pf) => pf.numberMonths === sessionPlan.plan.planSubscription,
                  ),
                  onSelectPaymentFrequency: () => null,
                  disabled: true,
                  showBanner: false,
                  currentSubscriptionUser: sessionPlan.plan.planSubscription,
                }}
                isMonthlySubscription={isMonthlySubscription}
                landingPacks={selectedLandingPacks}
                cancelLandings={cancelLandings}
                isEqualPlan={false}
                hidePromocode={true}
                buyType={BUY_LANDING_PACK}
                handleLandingPagesDowngrade={handleLandingPagesDowngrade}
                disabledLandingsBuy={
                  (isDowngrade && showArchiveLandings) ||
                  (!landingPagesRemoved &&
                    verifyIsTheSameLandingPacks(
                      contractedLandingPagesRef?.current,
                      selectedLandingPacks,
                    ))
                }
                landingPagesRemoved={landingPagesRemoved}
                selectedPaymentMethod={selectedPaymentMethod}
                billingCountry={billingCountry}
              />
            </div>
          </div>
        </div>
      </>
    );
  },
);
